import { useEffect, useMemo, useRef, FC, useState } from 'react'
import { Flex } from '@mantine/core'
import * as echarts from 'echarts'
import { Tabs } from '@gate/gui'
import type { ECharts } from 'echarts'
import { CoinChartsStyled } from '@/blocks/Price/PriceChart/style'
import BaseEmpty from '@/components/BaseEmpty'
import useRateChange from '@/hooks/useRateChange'
import type { PerformChartData, PlateItemData } from 'api-price'
import { useStore } from '@/store/index'
import { observer } from 'mobx-react-lite'
import useThemeVar from '@/hooks/useThemeStr'
import { EnumCoinLineTimeTab, colorMap, EnumCoinChartsTab } from '@/constants/index'
import { useAdapter } from '@/components/Adapter'
import { formatAddString, chartRiseColor, getTextWidth } from '@/utils/tools'
import {
  createNoSelectedSVG,
  createSelectedSVG,
  xAxisLabelTime,
  pageIconGather,
} from '@/blocks/Price/PriceChart/TrendChart'
import { ChartPerformBoxStyled, TypeTabsStyled } from './style'

type PerformChartProps = {
  data: { [key: string]: PerformChartData[] }
  lineTab: EnumCoinLineTimeTab[keyof EnumCoinLineTimeTab]
  plateData: PlateItemData[]
  plateSelector: string
  onPlateChange: (val: string) => void
  tabsValue: string & EnumCoinChartsTab[keyof EnumCoinChartsTab]
}

const PerformChart: FC<PerformChartProps> = ({ data, lineTab, plateData, plateSelector, tabsValue, onPlateChange }) => {
  const { isDark, riseColorValue } = useStore().globalStore
  const { isH5 } = useAdapter()

  const { themeVar } = useThemeVar()
  const { formatRateChangePrice } = useRateChange()
  const [isShowTab, setIsShowTab] = useState<boolean>(false)
  const [visibleIcon, setVisibleIcon] = useState<boolean>(false)

  const typeTabsRefs = useRef<HTMLDivElement | null>(null)

  const performChartRef = useRef<HTMLDivElement>(null) // 用于绑定 DOM 容器
  const performEchartsInstance = useRef<(echarts.EChartsType & any) | null>(null) // 保存 ECharts 实例

  useEffect(() => {
    if (isShowTab) return
    if (typeTabsRefs?.current) {
      // 监听tabs变化，防止高度塌陷问题
      setVisibleIcon(typeTabsRefs?.current?.scrollHeight > typeTabsRefs?.current?.clientHeight)
    }
  }, [tabsValue, isShowTab])

  /**
   * @description: echarts数据处理
   * @param {*} useMemo
   * @return {*}
   */
  const lineData = useMemo(() => {
    const xData = Object.keys(data)
    if (!xData?.length) return

    const dataValues = Object.values(data)

    // 数据 简称！集合
    const seriesNameGather = dataValues[dataValues?.length - 1]?.map((item: PerformChartData) => item?.coin_short_name)

    // 构造chart数组 | 取最后一组数据为基准
    const defineData = Object.values(data)?.map(item => {
      // 数据不齐补足数据
      if (item?.length !== xData?.length) {
        const itemData = seriesNameGather?.map(xItem => {
          const findCurrentItem = item?.find(curItem => curItem?.coin_short_name === xItem) as any

          if (findCurrentItem) {
            return findCurrentItem
          } else {
            return {
              coin_short_name: xItem,
              volatility: null,
              price: null,
              crawl_ts: findCurrentItem?.crawl_ts,
            }
          }
        })
        return itemData
      }

      return item
    })

    // 最大值 | 最小值 中间变量
    let maxValue = 0,
      minValue = 0

    const series = seriesNameGather?.map((item: PerformChartData['coin_short_name'], index: number) => {
      return {
        type: 'line',
        data: defineData?.map((dataItem: PerformChartData[]) => {
          const currentItem = dataItem?.find((curItem: PerformChartData) => curItem?.coin_short_name === item)
          // 最大值过滤 ｜ 计算grid
          if (Number(currentItem?.volatility) > maxValue) {
            maxValue = Number(currentItem?.volatility)
          }
          // 最小值过滤 ｜ 计算grid
          if (Number(currentItem?.volatility) < minValue) {
            minValue = Number(currentItem?.volatility)
          }
          return currentItem?.volatility
        }),
        name: item,
        smooth: false, // 关闭平滑曲线
        lineStyle: {
          color: colorMap[index],
          cap: 'butt', // 设置尖角
        },
        color: colorMap[index],
        showSymbol: false,
        emphasis: {
          focus: 'series',
        },
      }
    })

    const max = getTextWidth(formatAddString(maxValue) + '', 12, 'Gate_Sans,Arial,sans-serif')
    const min = getTextWidth(formatAddString(minValue) + '', 12, 'Gate_Sans,Arial,sans-serif')

    const leftPx = (max > min ? max : min) - 10

    return { xData, series, seriesNameGather, dataValues: defineData, leftPx, maxValue, minValue }
  }, [data])

  useEffect(() => {
    if (!performChartRef.current) return
    performEchartsInstance.current = echarts.init(performChartRef.current) as ECharts | undefined

    const options = {
      legend: {
        type: 'scroll',
        bottom: 0,
        orient: 'horizontal',
        itemGap: isH5 ? 16 : 40,
        itemWidth: 0,
        itemHeight: 0,
        selectedMode: 'multiple',
        pageTextStyle: {
          color: isDark ? '#FAFAFA' : '#262933',
        },
        pageIcons: {
          horizontal: [pageIconGather.left, pageIconGather.right],
        },
        pageIconColor: themeVar('--color-icon-1'),
        pageIconInactiveColor: themeVar('--color-icon-7'),
        pageIconSize: 12,
        formatter: (name: string) => {
          if (performEchartsInstance.current) {
            const selected = performEchartsInstance.current.getOption().legend[0].selected[name]
            return `{${name?.replaceAll(/[-(){} ]/g, '') + `${selected ? '' : '_selected'}`}|}{name|${name}}`
          }
        },
        textStyle: {
          rich: {
            name: {
              color: isDark ? '#FAFAFA' : '#262933',
              fontSize: 12,
              fontWeight: 500,
              padding: [12, 24, 12, 8],
            },
            ...lineData?.seriesNameGather?.reduce((obj: any, key, index) => {
              obj[key + '_selected'] = {
                width: 12,
                height: 12,
                backgroundColor: {
                  image: createNoSelectedSVG({ fill: colorMap[index] }),
                },
              }
              return obj
            }, {}),
            ...lineData?.seriesNameGather?.reduce((obj: any, key, index) => {
              obj[key] = {
                width: 12,
                height: 12,
                backgroundColor: {
                  image: createSelectedSVG({ fill: colorMap[index] }),
                },
              }
              return obj
            }, {}),
          },
        },
        selected: lineData?.seriesNameGather?.reduce((obj: any, key) => {
          obj[key] = true
          return obj
        }, {}),
      },
      grid: {
        left: lineData?.leftPx ? lineData.leftPx : 60,
        right: '36px',
        bottom: isH5 ? '94px' : '110px',
        top: '8px',
      },
      dataZoom: [
        {
          start: 0,
          end: 100,
          handleSize: isH5 ? '16px' : '24px',
          height: isH5 ? 16 : 24,
          backgroundColor: 'transparent',
          fillerColor: isDark ? 'rgba(38, 111, 255, 0.1)' : 'rgba(35, 84, 230, 0.1)',
          borderColor: isDark ? '#262933' : '#E6E9F2',
          dataBackground: {
            lineStyle: {
              color: isDark ? '#474F66' : '#E6E9F2',
              width: 1,
            },
            areaStyle: {
              color: isDark ? '#262933' : '#F8FAFD',
              opacity: 1,
            },
          },
          showDetail: false,
          bottom: '38px',
        },
      ],
      tooltip: {
        trigger: 'axis',
        confine: true,
        extraCssText: 'z-index: 20',
        padding: [12, 12, 12, 12],
        borderWidth: 0, // 提示框边框宽度
        backgroundColor: themeVar('--color-cmpt-6'), // 提示框背景色，深色模式和浅色模式
        // extraCssText: 'box-shadow: 0px 4px 24px 0px #04091914', // 提示框额外的CSS样式
        textStyle: {
          color: themeVar('--color-text-6'),
          fontSize: 12,
          fontWeight: 500,
          lineHeight: 1.5,
        },
        formatter: function (_params: any) {
          const sortParams = [..._params].sort((a, b) => {
            return b?.data - a?.data
          })
          // tooltip字符串组装
          let relVal = ''
          // 是否是原点
          const isDataIndexZero = sortParams[0]['dataIndex'] === 0

          let maxWidth = 0

          // 计算最大宽度
          sortParams.forEach((item: any) => {
            const currentValue = data[item?.axisValue]?.find(
              currentItem => currentItem?.coin_short_name === item?.seriesName
            ) as any

            const currentPrice = formatRateChangePrice(currentValue?.price, {
              isUnit: true,
              isThousands: true,
            })

            const currentWidth = getTextWidth(currentPrice + '', 12, 'Gate_Sans,Arial,sans-serif')
            if (currentWidth > maxWidth) {
              maxWidth = currentWidth
            }
          })

          for (let i = 0, l = sortParams.length; i < l; i++) {
            const item = sortParams[i]
            const currentValue = data[item?.axisValue]?.find(
              currentItem => currentItem?.coin_short_name === item?.seriesName
            ) as any
            const index = lineData?.seriesNameGather.findIndex(key => key === item?.seriesName) as number

            const currentPrice = formatRateChangePrice(currentValue?.price, {
              isUnit: true,
              isThousands: true,
            })

            relVal += `
              <div style="display: grid; grid-template-columns: 1fr 1fr;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="display: inline-block; border-radius: 3px; width: 6px; height: 6px; background-color: ${
                    colorMap?.[index]
                  };"></span>
                  <span style="font-size: 12px; font-weight: 700; color: ${themeVar('--color-text-6')};">${
                    item?.seriesName
                  }</span>
            </div>
              <div style="display: grid; gap: 8px; align-items: center; grid-template-columns: ${
                isDataIndexZero ? '1fr' : `${maxWidth ? `${maxWidth}px` : '1fr'} 1fr`
              };">
              <div style="font-size: 12px; font-weight: 500; display: flex; justify-content: flex-end; color: ${themeVar(
                '--color-text-6'
              )};">${currentPrice}</div>
                  <div style="font-size: 12px; font-weight: 700; text-align: right; color: ${chartRiseColor({
                    value: Number(item?.data),
                    market_rise_color: riseColorValue,
                  })}; display: ${isDataIndexZero ? 'none' : 'unset'};">${formatAddString(item?.data)}
                  </div>
              </div>
              </div>`
          }

          return `
            <div style="display: flex; flex-direction: column;gap: 8px; min-width: 200px;">
              <strong style="font-weight:500;font-size:16px;font-family:Gate_Sans">${xAxisLabelTime(
                sortParams[0]?.axisValue,
                lineTab
              )}</strong>
              ${relVal}
            </div>`
        },
      },
      xAxis: {
        data: lineData?.xData,
        boundaryGap: false,
        axisLine: {
          show: false,
          lineStyle: {
            color: themeVar('--color-divider-1'),
            width: 1,
          },
        },
        axisTick: {
          length: 8,
          alignWithLabel: true,
          lineStyle: {
            color: themeVar('--color-divider-1'),
            width: 0, // 隐藏刻度线
          },
          interval: 4,
          minInterval: 3600 * 24 * 1000,
        },
        axisLabel: {
          fontSize: isH5 ? 10 : 12,
          color: themeVar('--color-text-3'),
          fontWeight: 500,
          margin: 16,
          fontFamily: 'Gate_Sans',
          formatter: function (value: any, index: number) {
            if (index !== 0) {
              return xAxisLabelTime(value, lineTab)
            }
          },
        },
      },
      yAxis: {
        type: 'value',
        boundaryGap: [0, '10%'],
        splitNumber: 7,
        interval: Math.ceil(
          // 确保Y轴间距均匀，动态计算最大值最小值差
          Math.abs(Math.abs(lineData?.maxValue ?? 0) + Math.abs(lineData?.minValue ?? 0)) / 5
        ),
        splitLine: {
          lineStyle: {
            type: 'dashed', // 设置分隔线为虚线
            color: themeVar('--color-divider-2'),
            width: 1,
            opacity: 0.7,
          },
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: isDark ? '#2A2F40' : '#E6E9F2',
            width: 1,
          },
        },
        axisLabel: {
          formatter: function (value: any) {
            if (value === 0) return 0
            return `${value}%`
          },
          textStyle: {
            color: themeVar('--color-text-3'),
            fontSize: isH5 ? 10 : 12,
            fontWeight: 500,
            fontFamily: 'Gate_Sans',
          },
        },
      },
      series: lineData?.series,
      graphic: {
        type: 'image',
        z: -10,
        left: 'center',
        top: '30%',
        style: {
          image: '/price/public/images/icon-gt-logo.png',
          width: isH5 ? 90 : 180,
          height: isH5 ? 21 : 42,
          opacity: 0.8,
        },
      },
    }

    performEchartsInstance.current.setOption(options)

    //  监听图例选中事件  params: echarts.ECActionEvent
    performEchartsInstance.current.on('legendselectchanged', (params: any) => {
      const selected = params.selected

      const selectedArray: string[] = []

      // 找到当前选中数据的最小时间戳
      Object.keys(selected)?.forEach(item => {
        if (selected[item] === true) {
          selectedArray.push(item)
        }
      })

      const newDataObj: Record<string, any> = {}

      for (const [key, value] of Object.entries(data)) {
        const valueNameGather = value?.map((item: any) => item?.coin_short_name)

        // 判断当前选中数组和原数组有无交集、过滤x轴
        if (selectedArray?.some(item => valueNameGather.includes(item))) {
          newDataObj[key] = value
        }
      }

      const xData = Object.keys(newDataObj)

      // 构造chart数组 | 取最后一组数据为基准
      const defineData = Object.values(newDataObj)?.map(item => {
        // 数据不齐补足数据
        if (item?.length !== xData?.length) {
          const itemData = selectedArray?.map(xItem => {
            const findCurrentItem = item?.find((curItem: any) => curItem?.coin_short_name === xItem) as any

            if (findCurrentItem) {
              return findCurrentItem
            } else {
              return {
                coin_short_name: xItem,
                volatility: null,
                price: null,
                crawl_ts: findCurrentItem?.crawl_ts,
              }
            }
          })
          return itemData
        }

        return item
      })

      const currentSeries = selectedArray?.map((item: PerformChartData['coin_short_name'], index: number) => {
        const currentColor = options?.series?.find(optItem => optItem?.name === item)?.color ?? colorMap[index]
        return {
          type: 'line',
          data: defineData?.map((dataItem: PerformChartData[]) => {
            const currentItem = dataItem?.find((curItem: PerformChartData) => curItem?.coin_short_name === item)
            return currentItem?.volatility
          }),
          name: item,
          smooth: true,
          lineStyle: {
            color: currentColor,
          },
          color: currentColor,
          showSymbol: false,
          emphasis: {
            focus: 'series',
          },
        }
      })

      performEchartsInstance.current.setOption({
        xAxis: {
          data: xData,
        },
        series: currentSeries,
      })
    })

    const resizeHandler = (e: any) => {
      if (e?.[0].contentRect?.width !== performEchartsInstance.current.getWidth()) {
        performEchartsInstance.current.resize()
      }
    }

    const observer = new ResizeObserver(resizeHandler)

    if (performChartRef.current) {
      observer.observe(performChartRef.current)
    }

    window.addEventListener('resize', () => performEchartsInstance.current.resize())

    return () => {
      window.removeEventListener('resize', () => performEchartsInstance.current.resize())
      // myChart.dispose() 销毁实例。实例销毁后无法再被使用
      performEchartsInstance.current && performEchartsInstance.current.dispose()
      observer.disconnect()
    }
    // 不监听lineTab，会造成两次渲染
  }, [isDark, lineData, riseColorValue, formatRateChangePrice, themeVar, data, isH5])

  return (
    <CoinChartsStyled style={{ height: isH5 ? 'auto' : 410, minHeight: 285 }}>
      <ChartPerformBoxStyled>
        {/* tabs选项 */}
        <TypeTabsStyled>
          <Tabs
            type="fourth-capsule"
            value={plateSelector}
            onTabChange={(tab: string) => onPlateChange(tab)}
            styles={{
              tabsList: {
                height: isShowTab ? '100%' : '24px',
                overflow: 'hidden',
                padding: 0,
                rowGap: '8px',
              },
            }}
          >
            <Tabs.List ref={typeTabsRefs}>
              {plateData?.map(item => (
                <Tabs.Tab key={item?.id} value={item?.plate_unique_id}>
                  {item?.plate_en}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
          {visibleIcon ? (
            <div className="price-type-tab-icon" onClick={() => setIsShowTab(flag => !flag)}>
              <svg className="iconpark-icon">
                <use href={`#v31-16px-line-${isShowTab ? 'shangjiantou' : 'xiajiantou'}`}></use>
              </svg>
            </div>
          ) : null}
        </TypeTabsStyled>

        {Object.keys(data)?.length ? (
          <div key={`${isDark}-line-perform`} className="price-coin-perform-chart" ref={performChartRef} />
        ) : (
          <Flex h={isH5 ? 235 : 362} align="center">
            <BaseEmpty />
          </Flex>
        )}
      </ChartPerformBoxStyled>
    </CoinChartsStyled>
  )
}

export default observer(PerformChart)
